import * as Sentry from '@sentry/react'

/** Stable UI locations that request Google Maps data. Never record IDs, URLs, or addresses. */
export type GoogleMapsSource =
    | 'customer_saved_location'
    | 'customer_location_editor'
    | 'job_location'
    | 'job_location_editor'
    | 'driver_job_location'
    | 'job_creation_editor'
    | 'job_creation_review'
    | 'project_job_creation_editor'
    | 'project_job_creation_review'
    | 'quote_project_creation_editor'
    | 'equipment_yard_editor'
    | 'equipment_deployment_editor'
    | 'dispatch_run_map'

export type GoogleMapsOperationKind = 'dynamic_map' | 'place_details'
export type GoogleMapsInstance = 'new' | 'reused'

export type GoogleMapsOperationResult =
    | {outcome: 'ready'; reason: 'none'}
    | {
          outcome: 'error'
          reason:
              | 'script_load'
              | 'authentication'
              | 'missing_key'
              | 'runtime'
              | 'lookup_rejected'
              | 'missing_coordinates'
      }
    | {outcome: 'timeout'; reason: 'deadline'}
    | {outcome: 'cancelled'; reason: 'unmounted'}

export type GoogleMapsOperation = {
    /** Classifies the first observed map object; later calls are ignored. */
    recordInstance: (instance: GoogleMapsInstance) => void
    /** Records the terminal result and duration once; later calls are ignored. */
    finish: (result: GoogleMapsOperationResult) => void
}

export const GOOGLE_MAPS_OPERATION_DEADLINE_MS = 30_000

export const GOOGLE_MAPS_READY: GoogleMapsOperationResult = {outcome: 'ready', reason: 'none'}
export const GOOGLE_MAPS_CANCELLED: GoogleMapsOperationResult = {
    outcome: 'cancelled',
    reason: 'unmounted',
}

const GOOGLE_MAPS_TIMEOUT: GoogleMapsOperationResult = {outcome: 'timeout', reason: 'deadline'}

const observedMapObjects = new WeakSet<object>()

/**
 * Counts one started operation and returns a handle that records its single
 * outcome and duration. The source is captured here so asynchronous results
 * keep the attribution of the request that started them.
 */
export function startGoogleMapsOperation(
    source: GoogleMapsSource,
    operation: GoogleMapsOperationKind,
): GoogleMapsOperation {
    const startedAt = performance.now()
    let finished = false
    let instance: GoogleMapsInstance | 'none' = 'none'

    function finish(result: GoogleMapsOperationResult) {
        if (finished) {
            return
        }
        finished = true
        clearTimeout(deadlineTimer)

        const duration = performance.now() - startedAt
        // Background tabs can delay the timer, so readiness is judged by elapsed time too.
        const recorded =
            result.outcome === 'ready' && duration >= GOOGLE_MAPS_OPERATION_DEADLINE_MS
                ? GOOGLE_MAPS_TIMEOUT
                : result
        const attributes = {
            source,
            operation,
            outcome: recorded.outcome,
            reason: recorded.reason,
            ...(operation === 'dynamic_map' ? {instance} : {}),
        }
        Sentry.metrics.count('google_maps.operation_finished', 1, {attributes})
        Sentry.metrics.distribution('google_maps.operation_duration', duration, {
            unit: 'millisecond',
            attributes,
        })
        if (import.meta.env.MODE === 'development') {
            console.debug('[Google Maps]', {...attributes, duration})
        }
    }

    const deadlineTimer = setTimeout(
        () => finish(GOOGLE_MAPS_TIMEOUT),
        GOOGLE_MAPS_OPERATION_DEADLINE_MS,
    )
    Sentry.metrics.count('google_maps.operation_started', 1, {attributes: {source, operation}})

    return {
        finish,
        recordInstance: (observedInstance) => {
            if (!finished && instance === 'none') {
                instance = observedInstance
            }
        },
    }
}

/** Counts a dynamic map object once per document, under the source that first observed it. */
export function recordDynamicMapInstance(
    map: object,
    source: GoogleMapsSource,
): GoogleMapsInstance {
    if (observedMapObjects.has(map)) {
        return 'reused'
    }
    observedMapObjects.add(map)
    Sentry.metrics.count('google_maps.dynamic_instance', 1, {attributes: {source}})
    return 'new'
}
